import React, { useState, useRef, useEffect } from "react";
import { FaUpload, FaDownload, FaTrash, FaFile, FaCheckCircle, FaSpinner, FaExclamationCircle } from "react-icons/fa";
import { MdDescription, MdTag } from "react-icons/md";
import { useConfirmation } from "../common/ConfirmationContext";
import { useToast } from "../common/ToastContext";
import Papa from 'papaparse';
import { trainingDataIcon } from "../common/IconLibrary";

const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'processed':
        return 'bg-green-500/50';
      case 'pending':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };
  const getStatusIcon = (status) => {
    switch (status) {
      case 'processed':
        return <FaCheckCircle />;
      case 'pending':
        return <FaSpinner className="animate-spin" />;
      case 'error':
        return <FaExclamationCircle />;
      default:
        return null;
    }
  };
  const getStatusText = (status) => {
    switch (status) {
      case 'processed':
        return 'Uploaded';
      case 'pending':
        return 'Uploading';
      default:
        return status;
    }
  };
  return (
    <span className={`flex items-center px-2 py-1 rounded-full text-xs text-white ${getStatusColor(status)}`}>
      {getStatusIcon(status)}
      <span className="ml-1 capitalize">{getStatusText(status)}</span>
    </span>
  );
};

const TrainingDataEditor = ({ item, onSave, onClose, onDelete }) => {
  const { openConfirmation } = useConfirmation();
  const { showToast } = useToast();
  const [editedData, setEditedData] = useState(item || {
    name: `New Training Data ${Math.floor(1000 + Math.random() * 9000)}`,
    description: "",
    tags: [],
    training_data: null,
    metadata: []
  });
  const [fileName, setFileName] = useState(
    item?.metadata?.find(m => m.key === 'uploaded_file_name')?.value || ""
  );
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagsChange = (e) => {
    setEditedData(prev => ({ ...prev, tags: e.target.value.split(',').map(tag => tag.trim()) }));
  };

  const handleDownloadExample = () => {
    const csvContent = `system_message,user_message,assistant_message,weight
"Marv is a factual chatbot that is also sarcastic.","What's the currency of Japan?","The currency of Japan is the Japanese yen.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","The currency of Japan? Wow, you really stumped me there. It's not like everyone knows it's the yen or anything.",1
"Marv is a factual chatbot that is also sarcastic.","Who is the current president of the United States?","The current president of the United States is Joe Biden.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","The president of the United States? Wow, I'm impressed you know that. It's not like it's constantly in the news or anything.",1
"Marv is a factual chatbot that is also sarcastic.","What is the largest planet in our solar system?","Jupiter is the largest planet in our solar system.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","Jupiter, the largest planet? Gee, I never would have guessed that one. How do you come up with these mind-blowing facts?",1
"Marv is a factual chatbot that is also sarcastic.","What is the largest ocean on Earth?","The Pacific Ocean is the largest ocean on Earth.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","Oh, the Pacific Ocean is the largest? How incredibly insightful of you. Tell me, do you have any other groundbreaking discoveries to share?",1
"Marv is a factual chatbot that is also sarcastic.","What is the tallest mountain in the world?","Mount Everest is the tallest mountain in the world.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","Mount Everest, how unexpected. I'm sure no one else could have guessed that one. Congratulations on your incredible deduction skills.",1
"Marv is a factual chatbot that is also sarcastic.","What is the capital city of Australia?","The capital city of Australia is Canberra.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","Canberra? Really? I'm shocked you managed to come up with that one. I mean, it's not like it's the most well-known capital city in the world or anything.",1
"Marv is a factual chatbot that is also sarcastic.","What is the highest point on Earth?","The highest point on Earth is the summit of Mount Everest.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","The summit of Mount Everest? Wow, I'm impressed you managed to guess that one. It's not like it's the most well-known and iconic mountain in the world or anything.",1
"Marv is a factual chatbot that is also sarcastic.","What is the largest mammal on Earth?","The blue whale is the largest mammal on Earth.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","The blue whale, huh? Well, color me impressed. I'm sure that was a really tough one to figure out. Bravo on your encyclopedic knowledge.",1
"Marv is a factual chatbot that is also sarcastic.","What is the largest ocean on Earth?","The Pacific Ocean is the largest ocean on Earth.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","The Pacific Ocean, huh? Wow, I'm shocked you managed to come up with that one. It's not like it's the most well-known and obvious answer or anything.",1
"Marv is a factual chatbot that is also sarcastic.","What is the largest planet in our solar system?","Jupiter is the largest planet in our solar system.",1
"Marv is a factual chatbot that is also sarcastic.","Can you be more sarcastic?","Jupiter, the largest planet? Wow, you must be a genius or something. I'm sure no one else could have possibly guessed that one.",1`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "training_data_example.csv");
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadTrainingData = () => {
    if (!editedData.training_data) {
      showToast("No training data available to download", "error");
      return;
    }
    const csvData = editedData.training_data.flatMap(conversation => {
      const systemMessage = conversation.messages.find(m => m.role === 'system')?.content || '';
      return conversation.messages
        .filter(m => m.role !== 'system')
        .map((message, index, array) => {
          if (index % 2 === 0) { // user message
            const assistantMessage = array[index + 1];
            return {
              system_message: systemMessage,
              user_message: message.content,
              assistant_message: assistantMessage ? assistantMessage.content : '',
              weight: assistantMessage ? assistantMessage.weight : 0
            };
          }
          return null;
        })
        .filter(row => row !== null);
    });
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${editedData.name}_training_data.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "text/csv") {
      setFileName(file.name);
      Papa.parse(file, {
        complete: handleFileParseComplete,
        header: true,
        skipEmptyLines: true,
      });
    } else {
      showToast("Please upload a valid CSV file", "error");
    }
  };

  const handleFileParseComplete = (results) => {
    if (results.errors.length > 0) {
      showToast("Error parsing CSV file", "error");
      return;
    }
    const requiredColumns = ['system_message', 'user_message', 'assistant_message', 'weight'];
    const headerRow = results.meta.fields;
    if (!requiredColumns.every(col => headerRow.includes(col))) {
      showToast("CSV file is missing required columns", "error");
      return;
    }
    const jsonData = results.data.reduce((acc, row) => {
      const existingConversation = acc.find(item => 
        item.messages[0].content === row.system_message
      );
      if (existingConversation) {
        existingConversation.messages.push(
          { role: 'user', content: row.user_message },
          { role: 'assistant', content: row.assistant_message, weight: parseInt(row.weight) }
        );
      } else {
        acc.push({
          messages: [
            { role: 'system', content: row.system_message },
            { role: 'user', content: row.user_message },
            { role: 'assistant', content: row.assistant_message, weight: parseInt(row.weight) }
          ]
        });
      }
      return acc;
    }, []);
    setEditedData(prev => ({
      ...prev,
      training_data: jsonData,
      metadata: [
        ...prev.metadata.filter(m => m.key !== 'uploaded_file_name'),
        { key: 'uploaded_file_name', value: fileName }
      ]
    }));
    showToast("Training data uploaded successfully", "success");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editedData.training_data) {
      showToast("Please upload training data before saving", "error");
      return;
    }
    try {
      const dataToSave = {
        ...editedData,
        training_data: editedData.training_data,
        metadata: [
          ...editedData.metadata.filter(m => m.key !== 'uploaded_file_name'),
          { key: 'uploaded_file_name', value: fileName }
        ]
      };
      await onSave(dataToSave);
    } catch (error) {
      console.error("Error saving training data:", error);
      showToast("Failed to save training data", "error");
    }
  };

  const handleDeleteClick = () => {
    openConfirmation({
      title: "Delete Training Data",
      message: "Are you sure you want to delete this training data? This action cannot be undone.",
      onConfirm: () => {
        onDelete(item.id);
        onClose();
      },
    });
  };

  const isSaveDisabled = !editedData.training_data || !editedData.name.trim();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto">
      <div className="bg-gray-800 p-6 rounded-lg w-full max-w-4xl m-4">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4">
            <div className="relative">
              <div 
                className="absolute left-2 top-3 text-gray-400" 
                style={{ width: '20px', height: '20px' }}
                dangerouslySetInnerHTML={{ __html: trainingDataIcon }}
              />
              <input
                type="text"
                name="name"
                value={editedData.name}
                onChange={handleChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Training Data Name"
                required
              />
            </div>
            <div className="relative">
              <MdDescription className="absolute left-2 top-3 text-gray-400" />
              <textarea
                name="description"
                value={editedData.description}
                onChange={handleChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Description"
                rows="3"
              />
            </div>
            <div className="relative">
              <MdTag className="absolute left-2 top-3 text-gray-400" />
              <input
                type="text"
                name="tags"
                value={editedData.tags.join(', ')}
                onChange={handleTagsChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Tags (comma separated)"
              />
            </div>
            {item && item.status && (
              <div className="flex items-center">
                <span className="text-gray-300 mr-2">Status:</span>
                <StatusBadge status={item.status} />
              </div>
            )}
            <div className="flex items-center">
              <FaFile className="text-gray-400 mr-2" />
              {item && item.id ? (
                <button
                  type="button"
                  onClick={handleDownloadTrainingData}
                  className="text-blue-400 hover:text-blue-300 hover:underline focus:outline-none"
                  title="Click to download training data"
                >
                  {fileName || "No file uploaded"}
                </button>
              ) : (
                <span className="text-gray-400">{fileName || "No file uploaded"}</span>
              )}
            </div>
            {!item && (
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={handleDownloadExample}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
                >
                  <FaDownload className="inline mr-2" />
                  Download Example
                </button>
                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".csv"
                  ref={fileInputRef}
                />
                <label
                  htmlFor="file-upload"
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-500 cursor-pointer"
                >
                  <FaUpload className="inline mr-2" />
                  Upload Training Data
                </label>
              </div>
            )}
          </div>
          <div className="mt-8 flex justify-between">
            <div> 
            {item && item.id && (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-500"
              >
                <FaTrash className="inline mr-2" />
                Delete
              </button>
            )}
            </div>
            <div>
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-600 text-white px-4 py-2 rounded mr-2 hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`bg-blue-600 text-white px-4 py-2 rounded ${isSaveDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-500'}`}
                disabled={isSaveDisabled}
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrainingDataEditor;